import {
   FaBoxOpen,
   FaWallet,
   FaIdBadge,
   FaTruck,
   FaUserFriends,
   FaUserShield,
   FaFileInvoiceDollar,
   FaHandshake,
   FaKey,
} from 'react-icons/fa';

// ---------------------------------------------------------------------------
// Metadados de APRESENTAÇÃO do catálogo de permissões.
//
// A fonte da verdade é `GET /permissions` — este arquivo só dá rosto pt-BR
// (rótulo, ícone, descrição, ordem) ao que o backend declarar. Regra
// anti-drift: chave que o backend devolver e este mapa não conhecer NUNCA
// some da tela — cai no fallback cru (`humanize`). Permissão nova no backend
// aparece imediatamente, só que sem tradução, e a tradução é adicionada aqui
// depois.
// ---------------------------------------------------------------------------

export const MODULE_META = {
   inventory: {
      label: 'Estoque',
      icon: FaBoxOpen,
      description:
         'Movimentos, posição de estoque, produtos e transferências entre PDVs.',
      order: 1,
   },
   finance: {
      label: 'Financeiro',
      icon: FaWallet,
      description:
         'Cobranças, régua de vencidos, contas bancárias e dashboards.',
      order: 2,
   },
   customers: {
      label: 'Clientes',
      icon: FaUserFriends,
      description: 'Cadastro e manutenção da base de clientes.',
      order: 3,
   },
   hr: {
      label: 'Pessoas',
      icon: FaIdBadge,
      description: 'Funcionários, vales e recrutamento & seleção.',
      order: 4,
   },
   routing: {
      label: 'Roteirização',
      icon: FaTruck,
      description: 'Roteiros de serviço, itens de rota e produtividade.',
      order: 5,
   },
   commercial: {
      label: 'Comercial',
      icon: FaHandshake,
      description: 'Vendas, pedidos e apuração comercial.',
      order: 3,
   },
   tax: {
      label: 'Fiscal',
      icon: FaFileInvoiceDollar,
      description: 'Validações fiscais.',
      order: 6,
   },
   iam: {
      label: 'Configurações (IAM)',
      icon: FaUserShield,
      description: 'Contas de acesso, perfis e permissões do sistema.',
      order: 7,
   },
};

export const FALLBACK_MODULE_META = {
   label: null, // cai no humanize(module)
   icon: FaKey,
   description: '',
   order: 99,
};

// Rótulos por recurso (`{module}:{RECURSO}:{ação}`). Descrição é opcional —
// só onde ela ajuda o administrador a decidir.
export const RESOURCE_META = {
   // inventory
   stock: { label: 'Posição de estoque' },
   advanced_stock: { label: 'Pesquisa avançada de estoque' },
   movement: { label: 'Movimentos' },
   product: { label: 'Produtos' },
   category: { label: 'Categorias' },
   brand: { label: 'Marcas' },
   transfer: {
      label: 'Transferências entre PDVs',
      description: 'Ordens de transferência, despacho, recebimento e baixa.',
   },
   pdv: { label: 'PDVs' },

   // finance
   charge: { label: 'Cobranças (boletos)' },
   interaction: { label: 'Interações de cobrança' },
   dunning: { label: 'Régua de cobrança' },
   dunning_stage: { label: 'Raias da régua' },
   bank_account: { label: 'Contas bancárias' },
   credential: {
      label: 'Credenciais ASAAS',
      description: 'Chaves de integração — o valor nunca é exibido.',
   },
   dashboard: { label: 'Dashboards' },
   report: { label: 'Relatórios' },
   commission: { label: 'Comissões' },
   service_aquisition: { label: 'Aquisição de serviços' },
   service_aquisition_validation: { label: 'Validação de aquisição' },

   // hr
   employee: { label: 'Funcionários' },
   salary_advance: { label: 'Vales' },
   salary_payment: { label: 'Pagamentos de salário' },
   advance_request: { label: 'Solicitações de vale' },
   candidate: { label: 'Candidatos' },
   candidate_interview: { label: 'Entrevistas' },
   candidate_onboarding: { label: 'Onboarding' },
   job_opening: { label: 'Vagas' },
   job_position_requirement: { label: 'Requisitos de cargo' },
   recruitment_catalog: { label: 'Catálogos de recrutamento' },

   // routing
   schedule: { label: 'Roteiros (dia)' },
   schedule_item: { label: 'Itens de rota' },
   import: { label: 'Importação em lote' },
   service_catalog: { label: 'Catálogo de serviços' },
   subcluster: { label: 'Subclusters' },
   commission_entry: { label: 'Lançamentos de comissão' },

   // customers
   customer: { label: 'Clientes' },

   // iam
   user: { label: 'Usuários' },
   role: { label: 'Perfis de acesso' },
   permission: { label: 'Permissões' },
   iam_user_self: {
      label: 'Conta própria (self-service)',
      description: 'Perfil e senha do próprio usuário logado.',
   },
};

// As quatro colunas da matriz (ordem fixa do design).
export const CRUD_ACTIONS = ['read', 'create', 'update', 'delete'];

export const CRUD_ACTION_LABELS = {
   read: 'Ver',
   create: 'Criar',
   update: 'Editar',
   delete: 'Excluir',
};

// Qualquer ação fora do CRUD vira CHIP de ação extra na linha do recurso.
export const EXTRA_ACTION_LABELS = {
   settle: 'Baixar',
   cancel: 'Cancelar',
   manage: 'Gerir',
   print: 'Imprimir',
   dispatch: 'Despachar',
   receive: 'Receber',
   export: 'Exportar',
   search: 'Pesquisar',
   reconcile: 'Conciliar',
   interact: 'Interagir',
   external: 'Operador externo',
   read_all: 'Ver tudo',
   update_status: 'Alterar status',
   record_event: 'Registrar evento',
   remove_event: 'Remover evento',
   operate: 'Operar',
   create_financial: 'Validar (financeiro)',
   create_tax: 'Validar (fiscal)',
};

/** Fallback legível para slug sem tradução: `job_opening` → `Job opening`. */
export function humanize(slug) {
   if (!slug) return '';
   const text = String(slug).replace(/_/g, ' ').trim();
   return text.charAt(0).toUpperCase() + text.slice(1);
}

export function moduleMeta(moduleKey) {
   const meta = MODULE_META[moduleKey];
   if (meta) return meta;
   return { ...FALLBACK_MODULE_META, label: humanize(moduleKey) };
}

export function resourceLabel(resourceKey) {
   return RESOURCE_META[resourceKey]?.label ?? humanize(resourceKey);
}

export function resourceDescription(resourceKey) {
   return RESOURCE_META[resourceKey]?.description ?? '';
}

export function actionLabel(actionKey) {
   return (
      CRUD_ACTION_LABELS[actionKey] ??
      EXTRA_ACTION_LABELS[actionKey] ??
      humanize(actionKey)
   );
}
